/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useRef } from 'react'
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl'

import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiSplitPanel, useEuiTheme } from '@elastic/eui'
import {
  AreaSeries,
  Axis,
  Chart,
  LineSeries,
  Position,
  ScaleType,
  Settings,
  Tooltip,
  type PointerEvent,
  timeFormatter,
} from '@elastic/charts'

import { MetricTitle } from '@modules/auto-ops-components/MetricTitle/MetricTitle'
import { ChartCallout } from '@modules/auto-ops-components/ChartCallout/ChartCallout'
import {
  bytesToGigabytes,
  calculateDomain,
  generateCallout,
  getFormatFromMetricItem,
  truncateToFirstNonZero,
} from '@modules/autoops-lib/util'
import type { MetricItem } from '@modules/autoops-api/types'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'
import { useElasticChartsTheme } from '@modules/cui/charts/hooks'
import { useConfig } from '@modules/cui/ConfigContext'
import { TooltipHeader } from '@modules/auto-ops-components/TooltipHeader/TooltipHeader'

type ElasticsearchChartsProps = {
  data: Record<string, MetricItem | undefined>
}

const yAxisStyle = {
  tickLine: { visible: true, size: 0, padding: 10 },
  tickLabel: {
    alignment: {
      horizontal: Position.Left,
      vertical: Position.Bottom,
    },
    padding: 0,
    offset: { x: 0, y: 0 },
  },
}

const SOON_MESSAGES = defineMessages({
  index: {
    id: 'project.chart.soon.index',
    defaultMessage: 'Indexing performance metrics will soon be available in this region',
  },
  search: {
    id: 'project.chart.soon.search',
    defaultMessage: 'Search performance metrics will soon be available in this region',
  },
})

const generateSoonCallout = ({
  type,
}: {
  type: 'index' | 'search'
}): { type: 'info'; title: MessageDescriptor; message?: string } => ({
  type: 'info',
  title: SOON_MESSAGES[type],
})

const ElasticsearchCharts = ({ data }: ElasticsearchChartsProps) => {
  const { euiTheme } = useEuiTheme()
  const appName = useConfig('APP_NAME')
  const [isFlagUsable, flags] = useFlagsWhenLoaded()
  const isActivated =
    appName === 'adminconsole' || (isFlagUsable && flags.autoopsShowSearchTierPage)

  const baseTheme = useElasticChartsTheme()

  const { formatMessage } = useIntl()
  const {
    search_latency,
    search_rate,
    index_rate,
    index_latency,
    search_vcu,
    ingest_vcu,
    ml_vcu,
    storage_retained,
  } = data
  const hasSearchLatency = search_latency !== undefined && search_latency.error === null
  const hasSearchRate = search_rate !== undefined && search_rate.error === null
  const hasIndexLatency = index_latency !== undefined && index_latency.error === null
  const hasIndexRate = index_rate !== undefined && index_rate.error === null
  const hasSearchVCU = search_vcu !== undefined && search_vcu.error === null
  const hasIngestVCU = ingest_vcu !== undefined && ingest_vcu.error === null
  const hasMlVCU = ml_vcu !== undefined && ml_vcu.error === null
  const hasStorage = storage_retained !== undefined && storage_retained.error === null
  const refSearch = useRef<Chart>(null)
  const refIndex = useRef<Chart>(null)
  const refVCU = useRef<Chart>(null)
  const refStorage = useRef<Chart>(null)
  const searchCallout = generateCallout('search', [search_latency, search_rate])
  const indexCallout = generateCallout('index', [index_latency, index_rate])
  const vcuCallout = generateCallout('vcu', [search_vcu, ingest_vcu, ml_vcu])
  const storageCallout = generateCallout('storage_retained', [storage_retained])
  const { max: vcuMax } = calculateDomain(search_vcu, 0)
  const { max: storageMax } = calculateDomain(storage_retained, 0)

  const searchVCUData = search_vcu?.data ?? []
  const VCUFormat = getFormatFromMetricItem(searchVCUData)
  const VCUUnits = VCUFormat === 'MMM DD' ? '/d' : '/h'
  const storageData = storage_retained?.data ?? []
  const storageFormat = getFormatFromMetricItem(storageData)

  const pointerUpdate = (event: PointerEvent) => {
    refSearch.current?.dispatchExternalPointerEvent(event)
    refIndex.current?.dispatchExternalPointerEvent(event)
    refVCU.current?.dispatchExternalPointerEvent(event)
    refStorage.current?.dispatchExternalPointerEvent(event)
  }

  const IndexChart = () => {
    const { max: rateMaxDate } = calculateDomain(index_rate, 0)
    const { min: rateMin, max: rateMax } = calculateDomain(index_rate)
    const { min: latencyMin, max: latencyMax } = calculateDomain(index_latency)

    const rateData = index_rate?.data ?? []
    const latencyData = index_latency?.data ?? []
    const indexRateData = isActivated ? rateData : []
    const indexLatencyData = isActivated ? latencyData : []
    const callout = isActivated ? indexCallout : generateSoonCallout({ type: 'index' })

    const rateFormat = getFormatFromMetricItem(rateData)
    const rateUnits = rateFormat === 'MMM DD' ? '/d' : '/h'

    return (
      <EuiFlexItem>
        <EuiSplitPanel.Outer grow={true} hasBorder={true}>
          <EuiSplitPanel.Inner data-test-id='chart-panel'>
            <MetricTitle
              title='Indexing Rate / Indexing Latency'
              infoContent={
                <div>
                  <strong>Indexing rate</strong>
                  <p>
                    Total number of indexing requests executed across the project in each time
                    bucket over the selected time period.
                  </p>
                  <EuiSpacer size='m' />
                  <strong>Indexing latency</strong>
                  <p>
                    Peak project-level indexing latency, which is the time it takes to index all
                    incoming documents divided by their count in the given time period.
                  </p>
                </div>
              }
            />
            <EuiSpacer size='xs' />
            <Chart size={{ height: '200px' }} ref={refIndex}>
              <Settings
                baseTheme={baseTheme}
                theme={{
                  colors: {
                    vizColors: [euiTheme.colors.vis.euiColorVis2, euiTheme.colors.vis.euiColorVis3],
                  },
                }}
                noResults={
                  <ChartCallout
                    type={callout.type}
                    title={formatMessage(callout.title)}
                    message={callout.message}
                  />
                }
                showLegend={false}
                pointerUpdateDebounce={0}
                onPointerUpdate={pointerUpdate}
              />

              <Tooltip
                headerFormatter={({ value }) => (
                  <TooltipHeader value={value} max={rateMaxDate} format={rateFormat} />
                )}
              />

              <Axis id='bottom' position={Position.Bottom} showOverlappingTicks={true} />

              <Axis
                id='rate'
                groupId='rate'
                title='Indexing Rate'
                domain={{
                  min: rateMin,
                  max: rateMax,
                }}
                style={yAxisStyle}
                position={Position.Left}
              />

              <Axis
                id='latency'
                groupId='latency'
                title='Indexing Latency'
                domain={{
                  min: latencyMin,
                  max: latencyMax,
                }}
                style={yAxisStyle}
                position={Position.Right}
              />

              {hasIndexRate && (
                <LineSeries
                  key='Indexing Rate'
                  id='Indexing Rate'
                  groupId='rate'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={indexRateData}
                  tickFormat={(value: number) =>
                    `${truncateToFirstNonZero(Number(value))} ${rateUnits}`
                  }
                />
              )}
              {hasIndexLatency && (
                <LineSeries
                  key='Indexing Latency'
                  id='Indexing Latency'
                  groupId='latency'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={indexLatencyData}
                  tickFormat={(value: number) => `${truncateToFirstNonZero(Number(value))} ms`}
                />
              )}
            </Chart>
          </EuiSplitPanel.Inner>
        </EuiSplitPanel.Outer>
      </EuiFlexItem>
    )
  }

  const SearchChart = () => {
    const { max: rateMaxDate } = calculateDomain(search_rate, 0)
    const { min: rateMin, max: rateMax } = calculateDomain(search_rate)
    const { min: latencyMin, max: latencyMax } = calculateDomain(search_latency)

    const rateData = search_rate?.data ?? []
    const latencyData = search_latency?.data ?? []
    const searchRateData = isActivated ? rateData : []
    const searchLatencyData = isActivated ? latencyData : []
    const callout = isActivated ? searchCallout : generateSoonCallout({ type: 'search' })

    const rateFormat = getFormatFromMetricItem(rateData)
    const rateUnits = rateFormat === 'MMM DD' ? '/d' : '/h'

    return (
      <EuiFlexItem>
        <EuiSplitPanel.Outer grow={true} hasBorder={true}>
          <EuiSplitPanel.Inner data-test-id='chart-panel'>
            <MetricTitle
              title='Search Rate / Search Latency'
              infoContent={
                <div>
                  <strong>Search rate</strong>
                  <p>
                    Total number of search requests executed across the project in each time bucket
                    over the selected time period.
                  </p>
                  <EuiSpacer size='m' />
                  <strong>Search latency</strong>
                  <p>
                    Peak project-level search latency, which is the time it takes to execute all
                    incoming search requests divided by their count in the given time period.
                  </p>
                </div>
              }
            />
            <EuiSpacer size='xs' />
            <Chart size={{ height: '200px' }} ref={refSearch}>
              <Settings
                baseTheme={baseTheme}
                theme={{
                  colors: {
                    vizColors: [euiTheme.colors.vis.euiColorVis4, euiTheme.colors.vis.euiColorVis5],
                  },
                }}
                noResults={
                  <ChartCallout
                    type={callout.type}
                    title={formatMessage(callout.title)}
                    message={callout.message}
                  />
                }
                showLegend={false}
                pointerUpdateDebounce={0}
                onPointerUpdate={pointerUpdate}
              />

              <Tooltip
                headerFormatter={({ value }) => (
                  <TooltipHeader value={value} max={rateMaxDate} format={rateFormat} />
                )}
              />

              <Axis id='bottom' position={Position.Bottom} showOverlappingTicks={true} />

              <Axis
                id='rate'
                groupId='rate'
                title='Search Rate'
                domain={{
                  min: rateMin,
                  max: rateMax,
                }}
                style={yAxisStyle}
                position={Position.Left}
              />

              <Axis
                id='latency'
                groupId='latency'
                title='Search Latency'
                domain={{
                  min: latencyMin,
                  max: latencyMax,
                }}
                style={yAxisStyle}
                position={Position.Right}
              />

              {hasSearchRate && (
                <LineSeries
                  key='Search Rate'
                  id='Search Rate'
                  groupId='rate'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={searchRateData}
                  tickFormat={(value: number) =>
                    `${truncateToFirstNonZero(Number(value))} ${rateUnits}`
                  }
                />
              )}
              {hasSearchLatency && (
                <LineSeries
                  key='Search Latency'
                  id='Search Latency'
                  groupId='latency'
                  xScaleType={ScaleType.Time}
                  yScaleType={ScaleType.Linear}
                  xAccessor={0}
                  yAccessors={[1]}
                  data={searchLatencyData}
                  tickFormat={(value: number) => `${truncateToFirstNonZero(Number(value))} ms`}
                />
              )}
            </Chart>
          </EuiSplitPanel.Inner>
        </EuiSplitPanel.Outer>
      </EuiFlexItem>
    )
  }

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='l'>
        <EuiFlexItem>
          <EuiSplitPanel.Outer grow={true} hasBorder={true}>
            <EuiSplitPanel.Inner data-test-id='chart-panel'>
              <MetricTitle
                title='VCUs'
                infoContent='Amount of Virtual Compute Units (1 VCU contains 1GB of RAM and corresponding vCPU and local storage) allocated to each tier.'
              />
              <EuiSpacer size='xs' />
              <Chart size={{ height: '200px' }} ref={refVCU}>
                <Settings
                  baseTheme={baseTheme}
                  theme={{
                    colors: {
                      vizColors: [
                        euiTheme.colors.vis.euiColorVis4,
                        euiTheme.colors.vis.euiColorVis2,
                        euiTheme.colors.vis.euiColorVis8,
                      ],
                    },
                  }}
                  noResults={
                    <ChartCallout
                      type={vcuCallout.type}
                      title={formatMessage(vcuCallout.title)}
                      message={vcuCallout.message}
                    />
                  }
                  showLegend={true}
                  legendPosition={Position.Right}
                  pointerUpdateDebounce={0}
                  onPointerUpdate={pointerUpdate}
                />

                <Tooltip
                  headerFormatter={({ value }) => (
                    <TooltipHeader value={value} max={vcuMax} format={VCUFormat} />
                  )}
                />

                <Axis
                  id='bottom'
                  position={Position.Bottom}
                  tickFormat={(value) => timeFormatter(VCUFormat)(value)}
                />

                <Axis id='left' position={Position.Left} />

                {hasSearchVCU && (
                  <LineSeries
                    key='Search'
                    id='Search'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={searchVCUData}
                    tickFormat={(value: number) =>
                      `${truncateToFirstNonZero(Number(value))} ${VCUUnits}`
                    }
                  />
                )}
                {hasIngestVCU && (
                  <LineSeries
                    key='Ingest'
                    id='Ingest'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={ingest_vcu.data ?? []}
                    tickFormat={(value: number) =>
                      `${truncateToFirstNonZero(Number(value))} ${VCUUnits}`
                    }
                  />
                )}
                {hasMlVCU && (
                  <LineSeries
                    key='ML'
                    id='ML'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={ml_vcu.data ?? []}
                    tickFormat={(value: number) =>
                      `${truncateToFirstNonZero(Number(value))} ${VCUUnits}`
                    }
                  />
                )}
              </Chart>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiSplitPanel.Outer grow={true} hasBorder={true}>
            <EuiSplitPanel.Inner data-test-id='chart-panel'>
              <MetricTitle
                title='Storage retained'
                infoContent='Amount of data that is being retained in long-term object storage (subject to data retention settings).'
              />
              <EuiSpacer size='xs' />
              <Chart size={{ height: '200px' }} ref={refStorage}>
                <Settings
                  baseTheme={baseTheme}
                  noResults={
                    <ChartCallout
                      type={storageCallout.type}
                      title={formatMessage(storageCallout.title)}
                      message={storageCallout.message}
                    />
                  }
                  legendPosition={Position.Right}
                  pointerUpdateDebounce={0}
                  onPointerUpdate={pointerUpdate}
                />

                <Tooltip
                  headerFormatter={({ value }) => (
                    <TooltipHeader value={value} max={storageMax} format={storageFormat} />
                  )}
                />

                <Axis
                  id='bottom'
                  position={Position.Bottom}
                  tickFormat={(value) => timeFormatter(storageFormat)(value)}
                />

                <Axis id='left' position={Position.Left} />

                {hasStorage && (
                  <AreaSeries
                    key='Storage retained'
                    id='Storage retained'
                    xScaleType={ScaleType.Time}
                    yScaleType={ScaleType.Linear}
                    xAccessor={0}
                    yAccessors={[1]}
                    data={storageData.map(([x, y]) => [x, bytesToGigabytes(y)])}
                    tickFormat={(value: number) => `${truncateToFirstNonZero(Number(value))} GB`}
                  />
                )}
              </Chart>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='l' />
      <EuiFlexGroup gutterSize='l'>
        <IndexChart />
        <SearchChart />
      </EuiFlexGroup>
    </Fragment>
  )
}

export default ElasticsearchCharts
